// Sending a document comment to the agent (#13194 PR 2): the "Send to
// Finpilot" checkbox of the composer and the reply field, and the mention
// chip. Typing "@<agent name>" at the start of the field turns the mention
// into a chip and checks the box; unchecking the box takes the chip away;
// the box can also be checked with no mention. The agent's name is the one
// the surface gives it ("Finpilot" in chat, a named agent's own), never a
// model's.
import React, { useCallback, useState } from "react";
import { CheckSquare, Square } from "@phosphor-icons/react";

export interface AgentMention {
  /** Whether the comment is sent to the agent. */
  send: boolean;
  /** Whether the field leads with the agent's mention chip. */
  mention: boolean;
  toggle: () => void;
  /** The field's new text: a leading "@<name>" followed by a space leaves
   *  the text for the chip, which checks the box. */
  absorb: (value: string) => string;
  /** Backspace at the start of the field takes the chip away. */
  dropMention: () => void;
  /** The body to post: the mention, when there is one, leads it. */
  compose: (text: string) => string;
  /** Back to the field's starting state, after a post. */
  reset: () => void;
}

/** The checkbox and chip state for a field whose comment can go to the agent
 *  named `agentName` (null: it cannot, and the field is unchanged). */
export const useAgentMention = (
  agentName: string | null,
  defaultOn: boolean
): AgentMention => {
  const [send, setSend] = useState(defaultOn && agentName !== null);
  const [mention, setMention] = useState(false);
  const token = agentName ? `@${agentName}` : null;
  return {
    send: agentName !== null && send,
    mention: agentName !== null && mention,
    toggle: () => {
      if (send) {
        setSend(false);
        setMention(false);
      } else {
        setSend(true);
      }
    },
    absorb: (value) => {
      if (!token || mention) return value;
      const lead = value.slice(0, token.length);
      const after = value.charAt(token.length);
      if (lead.toLowerCase() !== token.toLowerCase() || !/\s/.test(after)) {
        return value;
      }
      setMention(true);
      setSend(true);
      return value.slice(token.length).replace(/^\s+/, "");
    },
    dropMention: () => {
      setMention(false);
      setSend(false);
    },
    compose: (text) => (mention && token ? `${token} ${text}` : text),
    reset: () => {
      setMention(false);
      setSend(defaultOn);
    },
  };
};

/** The mention at the head of a field, or of a comment sent with one. */
export const AgentMentionChip: React.FC<{
  name: string;
  chipRef?: (node: HTMLSpanElement | null) => void;
}> = ({ name, chipRef }) => (
  <span
    ref={chipRef}
    data-agent-mention=""
    className="inline-flex h-[20px] flex-none items-center rounded-[5px] bg-[#F7F8FF] px-1 text-[13px] font-medium leading-5 text-[#2A3DCB]"
  >
    @{name}
  </span>
);

/** The chip sits over the start of the field's first line, and the field's
 *  text starts after it (`textIndent`), so the comment reads as one run of
 *  text that wraps under the chip. */
export const useChipIndent = (): {
  chipRef: (node: HTMLSpanElement | null) => void;
  indent: number;
} => {
  const [indent, setIndent] = useState(0);
  const chipRef = useCallback(
    (node: HTMLSpanElement | null) =>
      setIndent(node ? node.offsetWidth + 4 : 0),
    []
  );
  return { chipRef, indent };
};

/** A comment's text, its leading mention of the agent drawn as the chip
 *  when the comment was sent to the agent with one. */
export const CommentBodyText: React.FC<{
  body: string;
  sentToAgent: boolean;
  agentName: string | null;
}> = ({ body, sentToAgent, agentName }) => {
  const token = agentName ? `@${agentName}` : null;
  if (
    !sentToAgent ||
    !token ||
    body.slice(0, token.length).toLowerCase() !== token.toLowerCase() ||
    !/\s/.test(body.charAt(token.length))
  ) {
    return <>{body}</>;
  }
  return (
    <>
      <AgentMentionChip name={body.slice(1, token.length)} />{" "}
      {body.slice(token.length).replace(/^\s+/, "")}
    </>
  );
};

/** The key code a browser gives a key an input method is processing. */
const IME_PROCESS_KEY_CODE = 229;

/** Whether a key in a message field sends it: Enter sends (Ctrl/Cmd+Enter
 *  too), Shift+Enter breaks the line, and an Enter that ends an input
 *  method's composition is the composition's, not a send. Safari reports
 *  that Enter with `isComposing` false and key code 229, so both count. */
export const isSendKey = (event: React.KeyboardEvent): boolean =>
  event.key === "Enter" &&
  !event.shiftKey &&
  !event.nativeEvent.isComposing &&
  event.nativeEvent.keyCode !== IME_PROCESS_KEY_CODE;

/** "Send to <agent>": the box, checked in the selection blue. */
export const SendToAgentCheckbox: React.FC<{
  name: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}> = ({ name, checked, onToggle, disabled = false }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    disabled={disabled}
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    className={`inline-flex h-6 min-w-0 cursor-pointer items-center gap-1.5 rounded-[6px] pr-1 text-[12.5px] disabled:cursor-default disabled:opacity-40 ${
      checked ? "text-[#2A3DCB]" : "text-[#5a6068] hover:text-[var(--fp-ink)]"
    }`}
  >
    {checked ? (
      <CheckSquare
        weight="fill"
        className="h-4 w-4 flex-none"
        aria-hidden="true"
      />
    ) : (
      <Square className="h-4 w-4 flex-none" aria-hidden="true" />
    )}
    <span className="truncate">Send to {name}</span>
  </button>
);
